import { useState, type CSSProperties } from 'react';
import type { CollectionCategory, QuestProgress } from '../../../core/domain/types';
import { clothing, crops, furniture, questById } from '../../../data/config';
import {
  collectionProgress,
  COLLECTION_REWARDS,
  discoveredCollectionIds,
  HOUSE_LEVEL_IDS,
  isNewCollectionItem,
} from '../../collection/domain/collectionRules';
import { useGameStore } from '../../../store/gameStore';
import { useUiStore } from '../../../store/uiStore';

interface CollectionEntry {
  id: string;
  name: string;
  visual: string;
  color?: string;
  detail: string;
}

const categoryLabels: Record<CollectionCategory, string> = {
  crop: '作物',
  furniture: '家具',
  clothing: '服装',
  house: '房屋',
};

const collectionEntries: Record<CollectionCategory, CollectionEntry[]> = {
  crop: crops.map((item) => ({
    id: item.id,
    name: item.name,
    visual: item.emoji,
    color: item.color,
    detail:
      (item.growSeconds < 3600
        ? item.growSeconds / 60 + '分钟'
        : item.growSeconds / 3600 + '小时') +
      '成熟 · 售价 ' +
      item.sellPrice,
  })),
  furniture: furniture.map((item) => ({
    id: item.id,
    name: item.name,
    visual: '▰',
    color: item.color,
    detail: item.theme + '主题 · ' + item.width + '×' + item.height + '格',
  })),
  clothing: clothing.map((item) => ({
    id: item.id,
    name: item.name,
    visual: '●',
    color: item.color,
    detail: '服装部位：' + item.slot + ' · Lv.' + item.unlockLevel,
  })),
  house: HOUSE_LEVEL_IDS.map((id, index) => ({
    id,
    name: '温馨小屋 Lv.' + (index + 1),
    visual: '🏡',
    detail: index === 4 ? '最高等级房屋' : '房屋成长阶段 ' + (index + 1),
  })),
};

function QuestCard({ entry }: { entry: QuestProgress }) {
  const definition = questById.get(entry.questId);
  const actions = useGameStore((state) => state.actions);
  const showNotice = useUiStore((state) => state.showNotice);
  if (!definition) return null;
  const ready = entry.progress >= definition.target && !entry.claimed;
  return (
    <article className="quest-card">
      <div>
        <h3>{definition.name}</h3>
        <p>
          {entry.progress}/{definition.target} · 🪙{definition.rewardCoins} ⭐{definition.rewardXp}
        </p>
      </div>
      <button
        disabled={!ready}
        onClick={() => {
          const result = actions.claimQuest(entry.questId);
          showNotice(result.ok ? '奖励已领取！' : result.message);
        }}
      >
        {entry.claimed ? '已领取' : ready ? '领取' : '进行中'}
      </button>
    </article>
  );
}

function CollectionCatalog() {
  const [category, setCategory] = useState<CollectionCategory>('furniture');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const game = useGameStore((state) => state.game);
  const actions = useGameStore((state) => state.actions);
  const showNotice = useUiStore((state) => state.showNotice);
  const entries = collectionEntries[category];
  const discovered = new Set(discoveredCollectionIds(game, category));
  const progress = collectionProgress(
    game,
    category,
    entries.map((entry) => entry.id),
  );
  const reward = COLLECTION_REWARDS[category];
  const selected = entries.find((entry) => entry.id === selectedId && discovered.has(entry.id));

  return (
    <div className="collection-section">
      <div className="collection-grid" role="tablist" aria-label="图鉴分类">
        {(Object.keys(categoryLabels) as CollectionCategory[]).map((itemCategory) => {
          const itemEntries = collectionEntries[itemCategory];
          const itemProgress = collectionProgress(
            game,
            itemCategory,
            itemEntries.map((entry) => entry.id),
          );
          const unseen = discoveredCollectionIds(game, itemCategory).filter((id) =>
            isNewCollectionItem(game, id),
          ).length;
          return (
            <button
              key={itemCategory}
              role="tab"
              aria-selected={category === itemCategory}
              className={category === itemCategory ? 'active' : ''}
              onClick={() => {
                setCategory(itemCategory);
                setSelectedId(null);
              }}
            >
              <strong>
                {itemProgress.discovered}/{itemProgress.total}
              </strong>
              <span>{categoryLabels[itemCategory]}</span>
              {unseen > 0 && <em>NEW {unseen}</em>}
            </button>
          );
        })}
      </div>

      <div className="collection-reward">
        <div>
          <strong>{categoryLabels[category]}收集奖励</strong>
          <span>
            集齐后获得 🪙{reward.coins} ⭐{reward.xp}
          </span>
        </div>
        <button
          disabled={!progress.complete || progress.claimed}
          onClick={() => {
            const result = actions.claimCollectionReward(category);
            showNotice(result.ok ? (result.message ?? '图鉴奖励已领取！') : result.message);
          }}
        >
          {progress.claimed ? '已领取' : progress.complete ? '领取奖励' : '尚未集齐'}
        </button>
      </div>

      <div className="collection-catalog">
        {entries.map((entry) => {
          const unlocked = discovered.has(entry.id);
          const isNew = unlocked && isNewCollectionItem(game, entry.id);
          return (
            <button
              key={entry.id}
              disabled={!unlocked}
              className={isNew ? 'collection-entry is-new' : 'collection-entry'}
              aria-label={unlocked ? '查看' + entry.name : '尚未发现'}
              onClick={() => {
                setSelectedId(entry.id);
                actions.markCollectionSeen(entry.id);
              }}
            >
              {isNew && <span className="new-badge">NEW</span>}
              <span
                className="collection-visual"
                style={{ '--collection-color': entry.color ?? '#e4b879' } as CSSProperties}
              >
                {unlocked ? entry.visual : '？'}
              </span>
              <strong>{unlocked ? entry.name : '未发现'}</strong>
              <small>{unlocked ? '点击查看' : '继续游玩解锁'}</small>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="collection-detail" role="status">
          <span
            className="collection-visual"
            style={{ '--collection-color': selected.color ?? '#e4b879' } as CSSProperties}
          >
            {selected.visual}
          </span>
          <div>
            <strong>{selected.name}</strong>
            <p>{selected.detail}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function QuestPage() {
  const game = useGameStore((state) => state.game);
  return (
    <section className="page-card quest-page">
      <div className="page-title">
        <div>
          <span className="eyebrow">今日目标</span>
          <h1>任务与图鉴</h1>
        </div>
        <span>{game.quests.dateKey}</span>
      </div>
      <h2>每日任务</h2>
      <div className="quest-list">
        {game.quests.daily.map((entry) => (
          <QuestCard key={entry.questId} entry={entry} />
        ))}
      </div>
      <h2>成长任务</h2>
      <div className="quest-list">
        {game.quests.growth.map((entry) => (
          <QuestCard key={entry.questId} entry={entry} />
        ))}
      </div>
      <h2>收集图鉴</h2>
      <CollectionCatalog />
    </section>
  );
}
